<template>
    <div
        v-if="activeThread || !contactNotFound || loading || mounting"
        :ref="(el) => (contactPane.containerRef.value = el)"
        class="relative flex flex-col gap-3 md:flex-row"
    >
        <div
            class="bg-white border border-solid border-white flex-1 flex-col relative overflow-hidden md:rounded-2xl md:shadow"
            :class="conversationClasses"
        >
            <div
                class="overflow-hidden flex flex-col grow"
                :style="{
                    paddingBottom: paddingBottom,
                }"
            >
                <div
                    class="bg-linear-to-b from-20% from-white to-white/0 h-12 inset-s-0 inset-e-2 absolute z-10 mdShortHeight:hidden"
                />

                <message-hub-conversation-nav></message-hub-conversation-nav>

                <conversation-messages>
                    <!-- spacer to keep content from getting hidden behind floating contact details -->
                    <div class="pt-40" aria-hidden="true"></div>
                </conversation-messages>
            </div>

            <div
                ref="messageBuilderWrapperRef"
                class="fixed flex flex-col gap-2 inset-s-0 inset-e-0 inset-be-0 shadow-[0px_12px_20px_0px_#000000] bg-white/80 backdrop-blur-sm p-4 md:relative md:inset-s-auto md:inset-e-auto md:inset-be-auto md:shadow-none md:bg-slate-50/90 md:rounded-b-2xl md:rounded-t-none md:border-solid md:border-x-0 md:border-b-0 md:border-t-slate-100 md:border-t mdShortHeight:p-4"
            >
                <conversation-input>
                    <template
                        v-if="existingTransactionalStatus !== 'opt_out' && isIntelligentRoute && !contactNotFound"
                        v-slot:before="{ focusTextarea }"
                    >
                        <message-hub-suggested-reply :focusTextarea="focusTextarea" />
                    </template>
                </conversation-input>
                <div class="rounded-xl overflow-hidden bg-white border border-solid border-slate-200 flex">
                    <div
                        v-if="canSnooze"
                        class="flex-1 flex [&>*]:flex-1 [&>*]:flex border-r border-solid border-slate-200 border-l-0 border-y-0"
                    >
                        <snooze-thread-dropdown
                            :thread="activeThread"
                            placement="right"
                            teleport
                            v-slot:default="{ snoozing, snoozed }"
                        >
                            <button
                                :disabled="!contact.phone"
                                class="appearance-none text-slate-800 bg-transparent w-full h-full gap-1 flex flex-col items-center justify-center py-2 px-2 hover:bg-slate-50 active:bg-slate-100 mdShortHeight:py-3 mdShortHeight:flex-row"
                                :class="{
                                    'opacity-50 cursor-default': snoozing || !contact.phone,
                                    'cursor-pointer': !snoozing && contact.phone,
                                }"
                            >
                                <font-awesome-icon
                                    widthAuto
                                    v-if="!snoozing"
                                    :icon="snoozed ? faClockRotateLeft : faClock"
                                    class="text-sm h-3.5"
                                ></font-awesome-icon>
                                <vx-icon-loading v-else spin class="w-3.5!"></vx-icon-loading>
                                <span class="text-[10px] leading-none text-slate-500 font-semibold">
                                    {{ snoozed ? 'Unsnooze' : 'Snooze' }}
                                </span>
                            </button>
                        </snooze-thread-dropdown>
                    </div>
                    <button
                        v-for="action in actions"
                        v-show="action.show"
                        :data-test="action.dataTest"
                        :disabled="!contact.phone"
                        :key="action.label"
                        @click="action.handler"
                        class="appearance-none text-slate-800 bg-transparent flex-1 gap-1 flex flex-col items-center justify-center py-2 px-2 border-r border-solid border-slate-200 border-l-0 border-y-0 hover:bg-slate-50 active:bg-slate-100 last:border-0 mdShortHeight:py-3 mdShortHeight:flex-row"
                        :class="{
                            'opacity-50 cursor-default': action.loading || !contact.phone,
                            'cursor-pointer': !action.loading && contact.phone,
                        }"
                    >
                        <font-awesome-icon
                            widthAuto
                            v-if="!action.loading"
                            :icon="action.icon"
                            :class="[action.color, 'text-sm h-3.5']"
                        ></font-awesome-icon>
                        <vx-icon-loading v-else spin class="w-3.5!"></vx-icon-loading>
                        <span class="text-[10px] leading-none text-slate-500 font-semibold">
                            {{ action.label }}
                        </span>
                    </button>
                </div>
            </div>
        </div>
        <message-hub-sidebar-resize-handle
            :enabled="contactPaneResizeEnabled"
            :isResizing="contactPane.isResizing.value"
            :handleStyle="contactPane.handleStyle.value"
            @start="contactPane.startResize"
        ></message-hub-sidebar-resize-handle>

        <!-- Only show MessageHubContactView when the the contact exists -->
        <div
            v-if="!contactNotFound && route.name === 'message-hub.contact-view'"
            :ref="(el) => (contactPane.panelRef.value = el)"
            class="flex h-full min-h-0 md:w-[380px] lg:w-[260px] 2xl:w-[424px]"
            :style="contactPane.panelStyle.value"
        >
            <router-view class="grow min-h-0"></router-view>
        </div>
        <message-builder-snippets
            v-model:visible="closeWithSnippetModal"
            @selected="closeWithSnippet($event)"
        ></message-builder-snippets>
        <message-builder-snippets
            v-model:visible="openWithSnippetModal"
            @selected="openWithSnippet($event)"
        ></message-builder-snippets>
        <message-hub-assign-modal
            v-if="activeThread"
            v-model:visible="assignAgentModal"
            :groupId="activeThread.group_id"
        ></message-hub-assign-modal>
        <message-hub-print-view />
    </div>
    <div v-else>
        <vx-box color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
            <div>Sorry, that content isn't available.</div>
            <router-button :to="{ name: 'message-hub.new-message', query: route.query }" size="lg" type="button">
                New Message
                <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
            </router-button>
        </vx-box>
    </div>
</template>

<script setup>
import {
    faCircleCheck,
    faCircleMinus,
    faCirclePlus,
    faClock,
    faClockRotateLeft,
    faEnvelope,
    faEnvelopeOpen,
    faMessageMinus,
    faMessagePlus,
    faUserMinus,
    faUserPen,
    faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxIconLoading, VxSwal } from '@voxie/frontend-components';
import { useResizeObserver } from '@vueuse/core';
import { computed, inject, onUnmounted, ref, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import MessageBuilderSnippets from '~/components/general/message-builder/modals/MessageBuilderSnippets.vue';
import RouterButton from '~/components/general/RouterButton.vue';
import ConversationInput from '~/components/messages/browser-extension/components/ConversationInput.vue';
import ConversationMessages from '~/components/messages/browser-extension/components/ConversationMessages.vue';
import { useConversation } from '~/composables/useConversation';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { hasScope } from '~/utils/users';
import SnoozeThreadDropdown from '~/components/message-hub/intelligent-inbox/SnoozeThreadDropdown.vue';
import MessageHubAssignModal from '~/components/message-hub/MessageHubAssignModal.vue';
import MessageHubConversationNav from '~/components/message-hub/MessageHubConversationNav.vue';
import MessageHubPrintView from '~/components/message-hub/MessageHubPrintView.vue';
import MessageHubSidebarResizeHandle from '~/components/message-hub/MessageHubSidebarResizeHandle.vue';
import MessageHubSuggestedReply from '~/components/message-hub/MessageHubSuggestedReply.vue';
import { useResizablePane } from '~/components/message-hub/useResizablePane';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import { useToasted } from '~/composables/useToasted';
import { threadsClient } from '~/services';
import debounce from '~/utils/debounce';
import Bus from '~/globals/eventBus';
import { catchRequestError } from '~/utils/request';
import { useMessageHub } from './useMessageHub';
import { canUseIntelligentInbox } from './messageHubUtil';

const FEATURES = inject('FEATURES');
const route = useRoute();
const { getCurrentGroupId } = useCurrentGroup();

const { isIntelligentRoute } = useMessageHub();

const {
    contact,
    contactNotFound,
    mount: mountContact,
    unmount: unmountContact,
    existingTransactionalStatus,
    loading,
} = useContactDetails();

const {
    mount: mountConversation,
    unmount: unmountConversation,
    mounting,
    activeThread,
    markAsRead,
    markAsUnread,
    togglingReadUnread,
    changeActiveThreadStatus,
    changingThreadStatus,
    closeWithSnippet,
    openWithSnippet,
    claimThread,
    unassignThread,
    changingAssignee,
} = useConversation();

watch(
    [() => route.params.contactId, () => route.query.group],
    // debounce is necessary here for two things:
    // 1. When setting the contact from the MessageHubSidebar -> MessageHubThreadRow
    // 2. When pressing the back and forward button on the browser window
    // 3. When navigating between inboxes in MessageHubNavbar the inbox change reuses a thread if it was already used
    debounce(async ([contactId, groupFromQuery]) => {
        const groupId = groupFromQuery || getCurrentGroupId();
        // only mounts if the contact from the route is different than the composable object
        // MessageHubThreadRow already PREFILLS the contact and conversation on click
        // MessageHubContactView already MOUNTS the contact on route changes
        if (Number(contactId) !== contact.value.id) {
            await mountContact(contactId, groupId);
        }

        mountConversation(undefined, groupId);
    }, 100),
    {
        immediate: true,
    }
);

onUnmounted(() => {
    // clean up, necessary because the global state of the composables are persisted
    // even after the route is no longer accessible, e.g. accessing the new message
    // or going to a different inbox
    unmountContact();
    unmountConversation();
});

const messageBuilderWrapperRef = ref();
const paddingBottom = ref(0);

useResizeObserver(messageBuilderWrapperRef, (elements) => {
    if (window.innerWidth > 768) {
        paddingBottom.value = 0;
        return;
    }

    paddingBottom.value = `${elements[0].target.offsetHeight}px`;
});

const conversationClasses = computed(() => {
    return route.name === 'message-hub.contact-view' ? 'hidden md:flex' : 'flex';
});

const { isMd } = useResponsiveLayout();

const contactPaneResizeEnabled = computed(() => {
    return isMd.value && route.name === 'message-hub.contact-view' && !contactNotFound.value;
});

const contactPane = useResizablePane({
    enabled: contactPaneResizeEnabled,
    side: 'end',
    storageKey: 'voxie.messageHub.contactViewWidth',
    minWidth: 260,
});

const closeWithSnippetModal = ref(false);
const openWithSnippetModal = ref(false);
const assignAgentModal = ref(false);

const canUseThreadActions = computed(() => canUseIntelligentInbox(FEATURES) && Boolean(activeThread.value));

const canSnooze = computed(() => canUseThreadActions.value);

const canComplete = computed(() => {
    return canUseThreadActions.value && activeThread.value?.thread_section !== 'completed';
});

const toasted = useToasted();
const completing = ref(false);

const completeThread = async () => {
    if (completing.value || !activeThread.value) {
        return;
    }

    const confirmed = await VxSwal.confirmation(
        'Mark as Done',
        "Are you sure? There is no undo. This thread will move to Done and stay there until it's recategorized (for example, when the contact replies)."
    );

    if (!confirmed) {
        return;
    }

    completing.value = true;
    try {
        const thread = activeThread.value;
        const context = { teamId: thread.team_id, groupId: thread.group_id };
        await threadsClient.complete(context, thread.contact_id);
        toasted.success('Thread completed.');
        Bus.$emit('threadUpdated', { thread: { ...thread, thread_section: 'completed' } });
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        completing.value = false;
    }
};

const completeAction = computed(() => ({
    icon: faCircleCheck,
    label: 'Done',
    dataTest: 'complete-thread',
    color: 'text-emerald-500',
    show: canComplete.value,
    loading: completing.value,
    handler: () => completeThread(),
}));

const actions = computed(() => {
    if (FEATURES.simple_message_hub) {
        return [
            completeAction.value,
            {
                icon: faEnvelope,
                label: 'Mark Unread',
                show: activeThread.value?.status !== 'open',
                loading: togglingReadUnread.value,
                handler: () => markAsUnread(activeThread.value),
            },
            {
                icon: faEnvelopeOpen,
                label: 'Mark Read',
                show: activeThread.value?.status === 'open',
                loading: togglingReadUnread.value,
                handler: () => markAsRead(activeThread.value),
            },
        ];
    }

    return [
        completeAction.value,
        {
            icon: faCircleMinus,
            label: 'Close',
            color: 'text-rose-500',
            show: activeThread.value?.status === 'open',
            loading: changingThreadStatus.value,
            handler: () => changeActiveThreadStatus(activeThread.value, 'closed'),
        },
        {
            icon: faMessageMinus,
            label: 'Close w/ Snippet',
            show:
                activeThread.value?.status === 'open' &&
                existingTransactionalStatus.value !== 'opt_out' &&
                !contactNotFound.value,
            loading: changingThreadStatus.value,
            handler: () => (closeWithSnippetModal.value = true),
        },
        {
            icon: faCirclePlus,
            label: 'Open',
            show: activeThread.value?.status !== 'open',
            loading: changingThreadStatus.value,
            handler: () => changeActiveThreadStatus(activeThread.value, 'open'),
        },
        {
            icon: faMessagePlus,
            label: 'Open w/ Snippet',
            show:
                activeThread.value?.status !== 'open' &&
                existingTransactionalStatus.value !== 'opt_out' &&
                !contactNotFound.value,
            loading: changingThreadStatus.value,
            handler: () => (openWithSnippetModal.value = true),
        },
        {
            icon: faEnvelope,
            label: 'Mark Unread',
            show: !activeThread.value?.unread,
            loading: togglingReadUnread.value,
            handler: () => markAsUnread(activeThread.value),
        },
        {
            icon: faEnvelopeOpen,
            label: 'Mark Read',
            show: activeThread.value?.unread,
            loading: togglingReadUnread.value,
            handler: () => markAsRead(activeThread.value),
        },
        {
            icon: faUserPlus,
            label: 'Claim',
            show:
                hasScope('threads:claim') &&
                !hasScope('threads:assign') &&
                activeThread.value?.assignee_id !== Spark.state.user.id,
            loading: changingAssignee.value,
            handler: () => claimThread(activeThread.value),
        },
        {
            icon: faUserMinus,
            label: 'Unclaim',
            show:
                hasScope('threads:claim') &&
                !hasScope('threads:assign') &&
                activeThread.value?.assignee_id === Spark.state.user.id,
            loading: changingAssignee.value,
            handler: () => unassignThread(activeThread.value),
        },
        {
            icon: faUserPen,
            label: 'Assign',
            show: hasScope('threads:assign'),
            loading: changingAssignee.value,
            handler: () => (assignAgentModal.value = true),
        },
    ];
});
</script>
